import { css } from "@linaria/core";
import cn from "classnames";
import React from "react";
import { Link } from "react-router-dom";
import { colors } from "../Theme";
import { MetroscopeLogoWhite } from "../IconV2/react";
import Text from "../Text";
import { styled } from "@linaria/react";

interface ApplicationHeaderProps {
  indexPath?: string;
  viewAction?: React.ReactNode;
  viewNavigation?: React.ReactNode;
  viewTitle?: React.ReactNode;
  viewTitleExtra?: React.ReactNode;
  withPortal?: boolean;
}

const breakpoints = {
  medium: "max-width: 960px",
  small: "max-width: 768px",
} as const;

const ApplicationHeader = css`
  align-items: center;
  background-color: ${colors.darkNitrogen900};
  color: ${colors.hydrogen};
  column-gap: 16px;
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  grid-template-rows: repeat(2, minmax(1fr, 56px));
  min-height: 56px;
  padding: 0 16px;
  width: 100%;
`;

const HomeLink = css`
  align-self: start;
  display: inline-block;
  height: 24px;
  margin-top: 16px;
  width: 16px;

  @media (${breakpoints.small}) {
    height: 100%;
    margin-top: 0;
  }

  svg {
    height: inherit;
    width: inherit;
  }
`;

const TopRow = css`
  gap: 8px;
  grid-row: 1 / span 1;
`;

const ViewSection = css`
  align-items: center;
  column-gap: 8px;
  display: flex;

  @media (${breakpoints.small}) {
    column-gap: 8px;
  }
`;

const ViewSectionWrappable = css`
  flex-wrap: wrap;
  grid-row: 1 / span 2;

  @media (${breakpoints.medium}) {
    display: contents;
  }
`;

const ViewHeader = css`
  flex-wrap: wrap;
  grid-column: 2 / span 1;
  min-height: 56px;
`;

const ViewHeaderTitle = css`
  align-items: center;
  column-gap: 8px;
  display: inline-flex;
  line-height: 35px;
  order: 0;
`;

const ViewTitleExtra = css`
  @media (${breakpoints.medium}) {
    display: contents;

    > :first-child {
      order: 2;
    }
    > :last-child {
      order: 1;
    }
  }
`;

const ViewNavigation = styled.div`
  &,
  > :first-child {
    height: 56px;
  }

  @media (${breakpoints.medium}) {
    grid-row: 2 / span 1;
    grid-column: 2 / span 2;
  }

  @media (${breakpoints.small}) {
    grid-column: 1 / span 3;
  }
`;

const ViewAction = css`
  align-self: start;
  grid-column: 3;
  min-height: 56px;

  @media (${breakpoints.small}) {
    justify-self: end;
  }
`;

/**
 * The header for the application.
 *
 * @param indexPath The path to the index page.
 * @param viewTitle The title of the current view.
 * @param viewTitleExtra Extra content to display next to the view title.
 * @param viewNavigation Navigation for the current view.
 * @param viewAction Actions for the current view.
 * @module ui/ApplicationHeader
 * @example
 * <ApplicationHeader
 *  indexPath={toPath({ key: "projects" })
 *  viewTitle="View Title"
 *  viewTitleExtra={<Text type="p">View Title Extra</Text>}
 *  viewNavigation={<div>View Navigation</div>}
 *  viewAction={<div>View Action</div>}
 * />
 */
export default React.memo(
  ({
    withPortal,
    indexPath,
    viewTitle,
    viewTitleExtra,
    viewNavigation,
    viewAction,
  }: ApplicationHeaderProps) => (
    <header className={cn(ApplicationHeader)}>
      {withPortal ? (
        <a href="/" className={cn(TopRow, HomeLink)}>
          <MetroscopeLogoWhite height="24px" width="16px" />
        </a>
      ) : (
        <Link to={indexPath || "/"} className={cn(TopRow, HomeLink)}>
          <MetroscopeLogoWhite height="24px" width="16px" />
        </Link>
      )}

      <div className={cn(ViewSection, ViewSectionWrappable)}>
        <div className={cn(ViewSection, ViewHeader)}>
          {viewTitle && (
            <Text type="h4" as="h1" className={ViewHeaderTitle}>
              {viewTitle}
            </Text>
          )}
          {viewTitleExtra && (
            <div className={cn(ViewSection, ViewTitleExtra)}>{viewTitleExtra}</div>
          )}
        </div>
        {viewNavigation && <ViewNavigation>{viewNavigation}</ViewNavigation>}
      </div>
      <div className={cn(TopRow, ViewSection, ViewAction)}>{viewAction}</div>
    </header>
  ),
);
